import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ActivitySquare, Scale, Ruler } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './WhRatioCalculatorPage/components/CalculationForm';
import ResultsOverview from './WhRatioCalculatorPage/components/ResultsOverview';
import ResultsVisualization from './WhRatioCalculatorPage/components/ResultsVisualization';
import QuickAdjustmentSidebar from './WhRatioCalculatorPage/components/QuickAdjustmentSidebar';
import SavedResultsSidebar from './WhRatioCalculatorPage/components/SavedResultsSidebar';
import TipsSidebar from './WhRatioCalculatorPage/components/TipsSidebar';
import InfoContent from './WhRatioCalculatorPage/components/InfoContent';
import { calculateWHR, getWHRCategory } from '../../../utils/calculations/health2';

// Определение начального состояния формы
export const initialFormData = {
  gender: 'female',
  waist: '70',
  hip: '100',
  age: '30',
  height: '165',
  weight: '65',
  measurementSystem: 'metric', // 'metric' или 'imperial'
  activityLevel: 'moderate' // 'sedentary', 'light', 'moderate', 'active', 'very_active'
};

// Определение категорий WHR для разных полов
export const whrCategories = {
  female: [
    { id: 'extreme_low', name: 'Экстремально низкий', max: 0.65, color: '#d32f2f', risk: 'Повышенный риск для здоровья при избыточной худобе' },
    { id: 'low', name: 'Низкий', min: 0.65, max: 0.75, color: '#ffa726', risk: 'Может указывать на недостаточный вес' },
    { id: 'normal', name: 'Нормальный', min: 0.75, max: 0.85, color: '#4caf50', risk: 'Минимальный риск для здоровья' },
    { id: 'high', name: 'Повышенный', min: 0.85, max: 0.9, color: '#ffa726', risk: 'Повышенный риск сердечно-сосудистых заболеваний и метаболического синдрома' },
    { id: 'extreme_high', name: 'Высокий', min: 0.9, color: '#d32f2f', risk: 'Высокий риск метаболического синдрома и заболеваний сердца' }
  ],
  male: [
    { id: 'extreme_low', name: 'Экстремально низкий', max: 0.85, color: '#d32f2f', risk: 'Повышенный риск для здоровья при избыточной худобе' },
    { id: 'low', name: 'Низкий', min: 0.85, max: 0.9, color: '#ffa726', risk: 'Может указывать на недостаточный вес' },
    { id: 'normal', name: 'Нормальный', min: 0.9, max: 0.95, color: '#4caf50', risk: 'Минимальный риск для здоровья' },
    { id: 'high', name: 'Повышенный', min: 0.95, max: 1.0, color: '#ffa726', risk: 'Повышенный риск сердечно-сосудистых заболеваний и метаболического синдрома' },
    { id: 'extreme_high', name: 'Высокий', min: 1.0, color: '#d32f2f', risk: 'Высокий риск метаболического синдрома и заболеваний сердца' }
  ]
};

// Определение уровней физической активности
export const activityLevels = [
  { id: 'sedentary', name: 'Сидячий образ жизни', description: 'Минимальная физическая активность, в основном сидячая работа' },
  { id: 'light', name: 'Лёгкая активность', description: 'Легкие упражнения 1-3 раза в неделю' },
  { id: 'moderate', name: 'Умеренная активность', description: 'Умеренные тренировки 3-5 раз в неделю' },
  { id: 'active', name: 'Высокая активность', description: 'Интенсивные тренировки 6-7 раз в неделю' },
  { id: 'very_active', name: 'Очень высокая активность', description: 'Тяжелые физические нагрузки ежедневно или 2 раза в день' }
];

/**
 * WhRatioCalculatorPage Component - Калькулятор соотношения талии и бедер с визуализацией и рекомендациями
 */
const WhRatioCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('whr-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор индекса талия-бедра (WHR) | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет соотношения объема талии к объему бедер (WHR) с оценкой рисков для здоровья, рекомендациями по питанию и физической активности, визуализацией и отслеживанием прогресса.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedResults, setSavedResults] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных результатов из localStorage ---
  useEffect(() => {
    const savedResultsData = localStorage.getItem('savedWhrResults');
    if (savedResultsData) {
      try {
        setSavedResults(JSON.parse(savedResultsData));
      } catch (e) {
        console.error("Error loading saved WHR results:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'female':
          newState.gender = 'female';
          break;
        case 'male':
          newState.gender = 'male';
          break;
        case 'elderly':
          newState.age = '65';
          break;
        case 'adolescent':
          newState.age = '16';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение результата в localStorage
  const saveResult = (name) => {
    if (!results) return;

    const newSavedResult = {
      id: Date.now().toString(),
      name: name || `WHR измерение ${new Date().toLocaleDateString('ru-RU')}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedResults = [...savedResults, newSavedResult];
    setSavedResults(updatedSavedResults);
    localStorage.setItem('savedWhrResults', JSON.stringify(updatedSavedResults));

    return newSavedResult;
  };

  // Удаление сохраненного результата
  const deleteSavedResult = (id) => {
    const updatedSavedResults = savedResults.filter(result => result.id !== id);
    setSavedResults(updatedSavedResults);
    localStorage.setItem('savedWhrResults', JSON.stringify(updatedSavedResults));
  };

  // Загрузка сохраненного результата
  const loadSavedResult = (id) => {
    const resultToLoad = savedResults.find(result => result.id === id);
    if (resultToLoad) {
      setFormData(resultToLoad.formData);
      setResults(resultToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <ActivitySquare className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор индекса талия-бедра (WHR)'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              whrCategories={whrCategories}
              activityLevels={activityLevels}
              saveResult={saveResult}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <ResultsVisualization results={results} formData={formData} />
              </>
            )}

            {/* --- Информационный контент о WHR --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сохраненных результатов --- */}
            <SavedResultsSidebar 
              savedResults={savedResults}
              deleteSavedResult={deleteSavedResult}
              loadSavedResult={loadSavedResult}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default WhRatioCalculatorPage;